import PropTypes from 'prop-types';
import { Box, Button, CircularProgress } from '@mui/material';
import { t } from 'i18next';
import { createContext, useState, useContext, Suspense } from 'react';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import BookOutlinedIcon from '@mui/icons-material/BookOutlined';
import LazyImage from '../../../../components/LazyImage/LazyImage';
import Tooltip from '../../../../components/Tooltip';

const fileStatuses = {
  loading: 'loading',
  success: 'success',
  error: 'error',
  idle: 'idle'
};

const fileReaderContext = createContext({});

const useFileReaderContext = () => {
  return useContext(fileReaderContext);
};

const getFileExtension = (fileName) => {
  return fileName?.split('.').at(-1);
};

const FileReaderProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({
    file: undefined,
    extension: undefined
  });

  const handleModal = () => {
    setOpen(!open);
  };

  const setFile = (file) => {
    setState(prev => ({
      ...prev,
      file,
      extension: getFileExtension(file.file_name)
    }));
  };

  const contextValues = {
    open,
    file: state.file,
    setFile,
    handleModal
  };

  return (<fileReaderContext.Provider value={contextValues} >
    {children}
    <ConfirmDialog
      open={open}
      message=''
      title={state.file?.file_name}
      cancelLabel={undefined}
      confirmLabel={t('download')}
      onClose={handleModal}
      showCustomBody
      disableActions
      renderCustomBody={ <>
        <Suspense
          fallback={<Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>}
        >
          <LazyImage src={state?.file?.url} width='100%'/>
        </Suspense>
      </>}
    >
    </ConfirmDialog>
  </fileReaderContext.Provider>);
};

FileReaderProvider.propTypes = {
  children: PropTypes.node
};

export const FileReaderButton = ({ file, variant }) => {
  const { setFile, handleModal } = useFileReaderContext();

  const [status] = useState(fileStatuses.idle);

  const isLoading = status === fileStatuses.loading;
  const isSuccess = status === fileStatuses.success;
  const isError = status === fileStatuses.error;
  const isIdle = status === fileStatuses.idle;

  const buttonStrategy = (() => {
    switch (getFileExtension(file?.file_type)) {
      case 'PROGRAM': return {
        tooltip: t('studentPresentedProgramTooltip'),
        title: t('ActivityProgram'),
        startIcon: <LibraryBooksOutlinedIcon/>,
        href: !isError && file?.url,
        target: '_blank',
        onClick: () => {
          setFile(file);
        }
      };
      case 'DU_FRONT': return {
        tooltip: t('DocumentFrontDescription'),
        title: t('DocumentFront'),
        startIcon: <BadgeOutlinedIcon/>,
        onClick: () => {
          if (isError) return;
          setFile(file);
          handleModal();
        }
      };
      case 'DU_BACK': return {
        tooltip: t('DocumentBackDescription'),
        title: t('DocumentBack'),
        startIcon: <BadgeIcon/>,
        onClick: () => {
          if (isError) return;
          setFile(file);
          handleModal();
        }
      };
      case 'SYLLABUS': return {
        tooltip: t('SyllabusProgramDescription'),
        title: t('SyllabusProgram'),
        startIcon: <BookOutlinedIcon/>,
        href: !isError && file?.url,
        target: '_blank',
        onClick: () => {
          setFile(file);
        }
      };
      case 'ACADEMIC_REPORT': return {
        tooltip: t('academicReportDescription'),
        title: t('academicReport'),
        startIcon: <LibraryBooksOutlinedIcon/>,
        href: !isError && file?.url,
        target: '_blank',
        onClick: () => {
          setFile(file);
        }
      };
      default: return {};
    }
  })();

  const getStatusImageLabel = (() => {
    if (isIdle) return t('requestingFileExistance');
    if (isSuccess) return buttonStrategy?.tooltip || '';
    if (isError) return t('fileNotExist');
    if (isLoading) return t('requestingFileExistance');
  })();

  return (<>
    {file
      ? <Tooltip title={getStatusImageLabel}>
        <Button
          variant={variant}
          startIcon={ <InsertPhotoIcon/>}
          endIcon={isLoading && <CircularProgress size='16px' /> }
          sx={{ padding: '0px 24px', height: '38px', marginRight: '5px', width: 'max-content' }}
          color={isError ? 'error' : undefined}
          onClick={() => {
            setFile(file);
          }}
          {...buttonStrategy}
        >
          {buttonStrategy.title || t('Archive')}
        </Button>
      </Tooltip>
      : null
    }
  </>);
};

FileReaderButton.propTypes = {
  file: PropTypes.object,
  variant:PropTypes.string
};

export default FileReaderProvider;
